import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiTool as Wrench, FiTrendingUp as TrendingUp, FiCheckCircle as CheckCircle, FiXCircle as XCircle, FiBarChart2 as BarChart3,
  FiChevronRight as ChevronRight, FiExternalLink as ExternalLink, FiRefreshCw as RefreshCw,
  FiActivity as Activity, FiTarget as Target, FiClock as Timer, FiAlertCircle as AlertCircle, FiHelpCircle as HelpCircle, FiInfo as Info,
  FiTrendingDown as TrendingDown, FiSun as Lightbulb
} from 'react-icons/fi';
import { getToolUsage } from '../../../../services/aiOperationsApi';
import { BLUE, GREEN, RED } from '../../../../constants/chartColors';
import { useProject } from '../../../../contexts/ProjectContext';
import { useFilter } from '../../../../contexts/FilterContext';

// Reusable Tooltip component
const Tooltip = ({ children, content, position = 'top' }: { children: React.ReactNode; content: React.ReactNode; position?: 'top' | 'bottom' | 'left' | 'right' }) => {
  const [show, setShow] = useState(false);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  return (
    <div className="relative inline-flex items-center">
      <div
        role="presentation"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {show && (
        <div className={`absolute z-50 ${positionClasses[position]} pointer-events-none`}>
          <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 max-w-xs shadow-lg">
            {content}
          </div>
        </div>
      )}
    </div>
  );
};

// Info badge with tooltip
const InfoBadge = ({ tooltip, className = '' }: { tooltip: React.ReactNode; className?: string }) => (
  <Tooltip content={tooltip}>
    <HelpCircle className={`w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ${className}`} />
  </Tooltip>
);

// Status indicator with explanation
const StatusIndicator = ({ value, thresholds, labels, reverse = false }: {
  value: number;
  thresholds: { good: number; warning: number };
  labels: { good: string; warning: string; critical: string };
  reverse?: boolean;
}) => {
  let color, label;

  if (reverse) {
    // For metrics where lower is better (like latency)
    if (value <= thresholds.good) {
      color = 'text-green-600 bg-green-100 dark:bg-green-900/30';
      label = labels.good;
    } else if (value <= thresholds.warning) {
      color = 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30';
      label = labels.warning;
    } else {
      color = 'text-red-600 bg-red-100 dark:bg-red-900/30';
      label = labels.critical;
    }
  } else {
    // For metrics where higher is better (like success rate)
    if (value >= thresholds.good) {
      color = 'text-green-600 bg-green-100 dark:bg-green-900/30';
      label = labels.good;
    } else if (value >= thresholds.warning) {
      color = 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30';
      label = labels.warning;
    } else {
      color = 'text-red-600 bg-red-100 dark:bg-red-900/30';
      label = labels.critical;
    }
  }

  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>
      {label}
    </span>
  );
};

// Mini sparkline component for trends
const Sparkline = ({ data, color = 'blue', height = 24 }: { data: Array<{ total?: number }>; color?: string; height?: number }) => {
  if (!data || data.length === 0) return null;

  const max = Math.max(...data.map(d => d.total || 0), 1);
  const width = 80;
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1 || 1)) * width;
    const y = height - ((d.total || 0) / max) * height;
    return `${x},${y}`;
  }).join(' ');

  // Calculate trend
  const firstHalf = data.slice(0, Math.floor(data.length / 2));
  const secondHalf = data.slice(Math.floor(data.length / 2));
  const firstAvg = firstHalf.reduce((s, d) => s + (d.total || 0), 0) / (firstHalf.length || 1);
  const secondAvg = secondHalf.reduce((s, d) => s + (d.total || 0), 0) / (secondHalf.length || 1);
  const trendUp = secondAvg > firstAvg * 1.1;
  const trendDown = secondAvg < firstAvg * 0.9;

  let trendLabel: string;
  if (trendUp) {
    trendLabel = 'Increasing usage';
  } else if (trendDown) {
    trendLabel = 'Decreasing usage';
  } else {
    trendLabel = 'Stable usage';
  }

  let strokeColor: string;
  if (color === 'blue') {
    strokeColor = BLUE;
  } else if (color === 'green') {
    strokeColor = GREEN;
  } else {
    strokeColor = RED;
  }

  return (
    <Tooltip content={
      <div>
        <p className="font-medium mb-1">Usage Trend</p>
        <p>{trendLabel}</p>
        <p className="text-gray-300 mt-1">Shows daily invocation pattern</p>
      </div>
    }>
      <div className="flex items-center gap-1">
        <svg width={width} height={height} className="overflow-visible">
          <polyline
            points={points}
            fill="none"
            stroke={strokeColor}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {trendUp && <TrendingUp className="w-3 h-3 text-green-500" />}
        {trendDown && <TrendingDown className="w-3 h-3 text-red-500" />}
      </div>
    </Tooltip>
  );
};

// Latency bar showing p50/p95/p99 with interactive tooltips
const LatencyBar = ({ p50, p95, p99, maxP99, toolName: _toolName }: { p50: number; p95: number; p99: number; maxP99: number; toolName: string }) => {
  const scale = (val: number) => Math.min((val / (maxP99 || 1)) * 100, 100);

  const formatMs = (seconds: number) => {
    if (!seconds) return '0ms';
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    return `${seconds.toFixed(2)}s`;
  };

  return (
    <Tooltip content={
      <div className="space-y-2">
        <p className="font-medium border-b border-gray-600 pb-1">Latency Distribution</p>
        <div className="space-y-1">
          <div className="flex justify-between gap-4">
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              p50 (median):
            </span>
            <span className="font-mono">{formatMs(p50)}</span>
          </div>
          <p className="text-gray-400 text-[10px] ml-3">50% of calls complete faster than this</p>

          <div className="flex justify-between gap-4 mt-2">
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 bg-yellow-400 rounded-full" />
              p95:
            </span>
            <span className="font-mono">{formatMs(p95)}</span>
          </div>
          <p className="text-gray-400 text-[10px] ml-3">95% of calls complete faster than this</p>

          <div className="flex justify-between gap-4 mt-2">
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 bg-red-300 rounded-full" />
              p99:
            </span>
            <span className="font-mono">{formatMs(p99)}</span>
          </div>
          <p className="text-gray-400 text-[10px] ml-3">99% of calls complete faster than this (worst case)</p>
        </div>
      </div>
    }>
      <div className="w-full">
        <div className="relative h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden cursor-help">
          <div
            className="absolute h-full bg-red-200 dark:bg-red-900/50 rounded-full transition-all"
            style={{ width: `${scale(p99)}%` }}
          />
          <div
            className="absolute h-full bg-yellow-400 dark:bg-yellow-600 rounded-full transition-all"
            style={{ width: `${scale(p95)}%` }}
          />
          <div
            className="absolute h-full bg-green-500 dark:bg-green-600 rounded-full transition-all"
            style={{ width: `${scale(p50)}%` }}
          />
        </div>
      </div>
    </Tooltip>
  );
};

// Metric Card with explanation
const MetricCard = ({ title, value, subtitle, icon: Icon, iconColor, tooltip, status, statusConfig }: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  iconColor: string;
  tooltip?: React.ReactNode;
  status?: boolean;
  statusConfig?: { thresholds: { good: number; warning: number }; labels: { good: string; warning: string; critical: string }; color?: string; reverse?: boolean };
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-sm text-gray-600 dark:text-gray-400">{title}</p>
          {tooltip && <InfoBadge tooltip={tooltip} />}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <p className={`text-2xl font-bold ${status ? statusConfig?.color || 'text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}`}>
            {value}
          </p>
          {status && statusConfig && (
            <StatusIndicator
              value={Number.parseFloat(String(value)) || 0}
              thresholds={statusConfig.thresholds}
              labels={statusConfig.labels}
              reverse={statusConfig.reverse}
            />
          )}
        </div>
        {subtitle && (
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        )}
      </div>
      <Icon className={`w-8 h-8 ${iconColor}`} />
    </div>
  </div>
);

interface ToolEntry {
  tool_name: string;
  total_uses: number;
  successful_uses: number;
  failed_uses: number;
  success_rate: number;
  avg_duration_seconds?: number;
  latency_percentiles?: { p50_seconds?: number; p95_seconds?: number; p99_seconds?: number };
  timeline?: Array<{ total?: number }>;
  error_breakdown?: Record<string, number>;
  recent_errors?: Array<{ run_id: string; status: string; action?: string }>;
}

interface ToolsSummary {
  total_tools?: number;
  total_invocations?: number;
  total_errors?: number;
  overall_success_rate?: number;
  latency_percentiles?: { p50_seconds?: number; p95_seconds?: number; p99_seconds?: number };
}

interface ToolsData {
  tools: ToolEntry[];
  summary: ToolsSummary;
}

const ToolsPage = () => {
  const { selectedProjectId } = useProject();
  const { filterType, selectedId: filterId } = useFilter();
  const navigate = useNavigate();
  const [toolsData, setToolsData] = useState<ToolsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  const [selectedTool, setSelectedTool] = useState<ToolEntry | null>(null);
  const [expandedErrors, setExpandedErrors] = useState<Record<string, boolean>>({});
  const [showGuide, setShowGuide] = useState(false);

  const loadToolsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getToolUsage({ days, include_timeline: true });
      setToolsData(data);
    } catch (err: unknown) {
      console.error('Error loading tools data:', err);
      setError((err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  }, [days, selectedProjectId, filterType, filterId]);

  useEffect(() => {
    loadToolsData();
  }, [loadToolsData]);

  const formatNumber = (num: number | null | undefined) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds && seconds !== 0) return 'N/A';
    if (seconds < 0.001) return '<1ms';
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    if (seconds < 60) return `${seconds.toFixed(2)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(0);
    return `${minutes}m ${secs}s`;
  };

  const tools: ToolEntry[] = toolsData?.tools || [];
  const summary: ToolsSummary = toolsData?.summary || {};

  const maxP99 = tools.length > 0
    ? Math.max(...tools.map((t) => t.latency_percentiles?.p99_seconds || 0))
    : 1;

  const handleToolClick = (tool: ToolEntry) => {
    setSelectedTool(selectedTool?.tool_name === tool.tool_name ? null : tool);
  };

  const toggleErrorExpand = (toolName: string) => {
    setExpandedErrors(prev => ({
      ...prev,
      [toolName]: !prev[toolName]
    }));
  };

  const navigateToTrace = (runId: string) => {
    navigate(`/ai-operations/traces/${runId}`);
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-300">Error loading tools data: {error}</p>
          <button
            onClick={loadToolsData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  let overallSuccessRateColor: string;
  if ((summary.overall_success_rate || 0) >= 95) {
    overallSuccessRateColor = 'text-green-600';
  } else if ((summary.overall_success_rate || 0) >= 80) {
    overallSuccessRateColor = 'text-yellow-600';
  } else {
    overallSuccessRateColor = 'text-red-600';
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
            Tool Usage Analytics
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
            Monitor tool invocations, latency percentiles, and error patterns
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowGuide(!showGuide)}
            className={`p-2 rounded-lg transition-colors ${
              showGuide
                ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title="Toggle metric guide"
          >
            <Lightbulb className="w-5 h-5" />
          </button>
          <button
            onClick={loadToolsData}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Refresh data"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Quick Guide Panel */}
      {showGuide && (
        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-3 text-sm">
              <h3 className="font-semibold text-blue-900 dark:text-blue-100">Understanding This Dashboard</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-800 dark:text-blue-200">
                <div>
                  <p className="font-medium flex items-center gap-1">
                    <Target className="w-4 h-4" /> Success Rate
                  </p>
                  <p className="text-blue-700 dark:text-blue-300 text-xs mt-1">
                    Percentage of tool calls that completed successfully.{' '}
                    <span className="text-green-600">Green (&ge;95%)</span> is healthy,{' '}
                    <span className="text-yellow-600">Yellow (80-95%)</span> needs attention,{' '}
                    <span className="text-red-600">Red (&lt;80%)</span> is critical.
                  </p>
                </div>
                <div>
                  <p className="font-medium flex items-center gap-1">
                    <Timer className="w-4 h-4" /> Latency Percentiles (p50/p95/p99)
                  </p>
                  <p className="text-blue-700 dark:text-blue-300 text-xs mt-1">
                    <strong>p50</strong>: Median response time (typical user experience).{' '}
                    <strong>p95</strong>: 95% of requests are faster.{' '}
                    <strong>p99</strong>: Slowest 1% of requests (edge cases).
                  </p>
                </div>
                <div>
                  <p className="font-medium flex items-center gap-1">
                    <Activity className="w-4 h-4" /> Invocations
                  </p>
                  <p className="text-blue-700 dark:text-blue-300 text-xs mt-1">
                    Total number of times each tool was called. Higher usage tools have more impact on system performance.
                  </p>
                </div>
                <div>
                  <p className="font-medium flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" /> Usage Trend
                  </p>
                  <p className="text-blue-700 dark:text-blue-300 text-xs mt-1">
                    Sparkline shows usage pattern over time. Useful for identifying usage spikes or drops.
                  </p>
                </div>
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-400 border-t border-blue-200 dark:border-blue-700 pt-2 mt-2">
                Hover over any metric or the <HelpCircle className="w-3 h-3 inline" /> icon for detailed explanations. Click on a tool row to see detailed breakdown.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Time Range Filter */}
      <div className="mb-6 flex items-center gap-4">
        <label htmlFor="time-range" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Time Range:
        </label>
        <select id="time-range"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                   focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <Tooltip content="Select the time window for aggregating tool metrics. Longer periods show broader trends.">
          <HelpCircle className="w-4 h-4 text-gray-400" />
        </Tooltip>
      </div>

      {/* Summary Cards with tooltips */}
      <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Tools"
          value={formatNumber(summary.total_tools || tools.length)}
          icon={Wrench}
          iconColor="text-blue-600"
          tooltip="Number of unique tools used by agents in the selected time period."
        />

        <MetricCard
          title="Total Invocations"
          value={formatNumber(summary.total_invocations || 0)}
          subtitle={(summary.total_errors ?? 0) > 0 ? `${formatNumber(summary.total_errors ?? 0)} errors (${(((summary.total_errors ?? 0) / (summary.total_invocations ?? 1)) * 100).toFixed(1)}%)` : 'No errors'}
          icon={Activity}
          iconColor="text-green-600"
          tooltip="Total number of tool calls across all tools. Higher numbers indicate heavier tool usage."
        />

        <MetricCard
          title="Success Rate"
          value={`${(summary.overall_success_rate || 0).toFixed(1)}%`}
          icon={Target}
          iconColor="text-purple-600"
          tooltip="Percentage of tool invocations that completed successfully without errors."
          status={true}
          statusConfig={{
            thresholds: { good: 95, warning: 80 },
            labels: { good: 'Healthy', warning: 'Needs Attention', critical: 'Critical' },
            color: overallSuccessRateColor
          }}
        />

        <MetricCard
          title="P95 Latency"
          value={formatDuration(summary.latency_percentiles?.p95_seconds || 0)}
          subtitle={`Median: ${formatDuration(summary.latency_percentiles?.p50_seconds || 0)}`}
          icon={Timer}
          iconColor="text-orange-600"
          tooltip={
            <div>
              <p className="font-medium">95th Percentile Latency</p>
              <p className="mt-1">95% of all tool calls complete faster than this time.</p>
              <p className="mt-1 text-gray-300">This is a good measure of "worst typical" performance - it excludes extreme outliers while still showing slow requests.</p>
            </div>
          }
        />
      </div>

      {/* Column Headers Legend */}
      <div className="mb-2 px-6 hidden lg:flex items-center gap-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        <div className="w-48">Tool Name</div>
        <div className="w-24 text-center">
          <Tooltip content="Total number of times this tool was invoked">
            <span className="cursor-help">Calls</span>
          </Tooltip>
        </div>
        <div className="w-24 text-center">
          <Tooltip content="Percentage of successful invocations. Green = good, Yellow = warning, Red = critical">
            <span className="cursor-help">Success</span>
          </Tooltip>
        </div>
        <div className="flex-1 max-w-xs text-center">
          <Tooltip content={
            <div>
              <p className="font-medium">Latency Distribution</p>
              <p className="mt-1">Visual comparison of p50, p95, and p99 latencies.</p>
              <p className="mt-1">Longer bars = slower performance relative to other tools.</p>
            </div>
          }>
            <span className="cursor-help">Latency (p50 / p95 / p99)</span>
          </Tooltip>
        </div>
        <div className="w-24 text-center">
          <Tooltip content="Daily usage pattern over the selected time period. Shows trends.">
            <span className="cursor-help">Trend</span>
          </Tooltip>
        </div>
        <div className="w-5"></div>
      </div>

      {/* Tools Performance Grid */}
      <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm transition-colors overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Tool Performance
            </h2>
            <InfoBadge tooltip="Click on any tool row to see detailed statistics, error breakdown, and recent failures." />
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <div className="w-3 h-2 bg-green-500 rounded" /> p50
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-2 bg-yellow-400 rounded" /> p95
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-2 bg-red-200 rounded" /> p99
            </span>
          </div>
        </div>

        {tools.length === 0 ? (
          <div className="p-12 text-center">
            <Wrench className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">No tool usage data available</p>
            <p className="text-sm text-gray-500 mt-1">Tools will appear here once agents start using them</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {tools.map((tool: ToolEntry, _index: number) => {
              const isSelected = selectedTool?.tool_name === tool.tool_name;
              const hasErrors = (tool.failed_uses || 0) > 0;
              const successRate = tool.success_rate || 0;

              let successRateColor: string;
              if (successRate >= 95) {
                successRateColor = 'text-green-600';
              } else if (successRate >= 80) {
                successRateColor = 'text-yellow-600';
              } else {
                successRateColor = 'text-red-600';
              }

              let successRateTooltipText: string;
              if (successRate >= 95) {
                successRateTooltipText = 'Excellent - tool is performing well';
              } else if (successRate >= 80) {
                successRateTooltipText = 'Warning - some failures occurring';
              } else {
                successRateTooltipText = 'Critical - many failures, investigate immediately';
              }

              return (
                <div key={tool.tool_name}>
                  {/* Tool Row */}
                  <div
                    onClick={() => handleToolClick(tool)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleToolClick(tool); }} role="button" tabIndex={0}
                    className={`px-6 py-4 cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Tool Name & Icon */}
                      <div className="flex-shrink-0 w-48">
                        <div className="flex items-center gap-2">
                          <Wrench className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900 dark:text-white truncate">
                            {tool.tool_name}
                          </span>
                          {hasErrors && (
                            <Tooltip content={`${tool.failed_uses} failed calls - click to see details`}>
                              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                            </Tooltip>
                          )}
                        </div>
                      </div>

                      {/* Invocations */}
                      <div className="w-24 text-center">
                        <Tooltip content={
                          <div>
                            <p className="font-medium">{formatNumber(tool.total_uses)} total calls</p>
                            <p className="text-green-400">{formatNumber(tool.successful_uses)} successful</p>
                            {tool.failed_uses > 0 && <p className="text-red-400">{formatNumber(tool.failed_uses)} failed</p>}
                          </div>
                        }>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatNumber(tool.total_uses)}
                            </p>
                            <p className="text-xs text-gray-500">calls</p>
                          </div>
                        </Tooltip>
                      </div>

                      {/* Success Rate */}
                      <div className="w-24 text-center">
                        <Tooltip content={
                          <div>
                            <p className="font-medium">{successRate.toFixed(1)}% success rate</p>
                            <p className="mt-1 text-gray-300">
                              {successRateTooltipText}
                            </p>
                          </div>
                        }>
                          <div>
                            <p className={`text-sm font-medium ${successRateColor}`}>
                              {successRate.toFixed(1)}%
                            </p>
                            <p className="text-xs text-gray-500">success</p>
                          </div>
                        </Tooltip>
                      </div>

                      {/* Latency Percentiles Bar */}
                      <div className="flex-1 max-w-xs">
                        <LatencyBar
                          p50={tool.latency_percentiles?.p50_seconds || 0}
                          p95={tool.latency_percentiles?.p95_seconds || 0}
                          p99={tool.latency_percentiles?.p99_seconds || 0}
                          maxP99={maxP99}
                          toolName={tool.tool_name}
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{formatDuration(tool.latency_percentiles?.p50_seconds || 0)}</span>
                          <span>{formatDuration(tool.latency_percentiles?.p95_seconds || 0)}</span>
                          <span>{formatDuration(tool.latency_percentiles?.p99_seconds || 0)}</span>
                        </div>
                      </div>

                      {/* Sparkline Trend */}
                      <div className="w-24 flex justify-center">
                        {(tool.timeline?.length ?? 0) > 1 ? (
                          <Sparkline data={tool.timeline!} color="blue" />
                        ) : (
                          <Tooltip content="Not enough data points for trend visualization">
                            <span className="text-xs text-gray-400">-</span>
                          </Tooltip>
                        )}
                      </div>

                      {/* Expand Arrow */}
                      <Tooltip content={isSelected ? 'Click to collapse' : 'Click to expand details'}>
                        <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${
                          isSelected ? 'rotate-90' : ''
                        }`} />
                      </Tooltip>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isSelected && (
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Detailed Stats */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Detailed Statistics
                            </h4>
                            <InfoBadge tooltip="Comprehensive breakdown of tool performance metrics" />
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Total Calls</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {formatNumber(tool.total_uses)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-green-500" /> Successful
                              </span>
                              <span className="font-medium text-green-600">
                                {formatNumber(tool.successful_uses)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                                <XCircle className="w-3 h-3 text-red-500" /> Failed
                              </span>
                              <span className="font-medium text-red-600">
                                {formatNumber(tool.failed_uses)}
                              </span>
                            </div>
                            <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                              <div className="flex justify-between">
                                <Tooltip content="Average time for a tool call to complete">
                                  <span className="text-gray-600 dark:text-gray-400 cursor-help">Avg Duration</span>
                                </Tooltip>
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {formatDuration(tool.avg_duration_seconds)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Error Breakdown */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Error Breakdown
                            </h4>
                            <InfoBadge tooltip="Categorization of failures by error type. Helps identify root causes." />
                          </div>
                          {Object.keys(tool.error_breakdown || {}).length === 0 ? (
                            <div className="flex items-center gap-2 text-sm text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              No errors in this period
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {Object.entries(tool.error_breakdown ?? {}).map(([status, count]) => (
                                <div key={status} className="flex justify-between text-sm">
                                  <Tooltip content={`${count} failures with status: ${status}`}>
                                    <span className="text-gray-600 dark:text-gray-400 capitalize cursor-help">
                                      {status.replaceAll('_', ' ')}
                                    </span>
                                  </Tooltip>
                                  <span className="font-medium text-red-600">{count}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Recent Errors */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Recent Errors
                            </h4>
                            <InfoBadge tooltip="Most recent failures. Click to navigate to the full trace for debugging." />
                          </div>
                          {(!tool.recent_errors || tool.recent_errors.length === 0) ? (
                            <p className="text-sm text-gray-500">No recent errors</p>
                          ) : (
                            <div className="space-y-2">
                              {tool.recent_errors.slice(0, 3).map((error) => (
                                <div
                                  key={error.run_id}
                                  onClick={(e) => { e.stopPropagation(); navigateToTrace(error.run_id); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); navigateToTrace(error.run_id); } }} role="button" tabIndex={0}
                                  className="p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors group"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-red-700 dark:text-red-300 font-medium capitalize">
                                      {error.status}
                                    </span>
                                    <span className="flex items-center gap-1 text-red-500 group-hover:text-red-700">
                                      <span className="text-[10px]">View trace</span>
                                      <ExternalLink className="w-3 h-3" />
                                    </span>
                                  </div>
                                  <p className="text-red-600 dark:text-red-400 truncate mt-1">
                                    {error.action || 'No action details'}
                                  </p>
                                </div>
                              ))}
                              {tool.recent_errors.length > 3 && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleErrorExpand(tool.tool_name); }}
                                  className="text-xs text-blue-600 hover:text-blue-800"
                                >
                                  {expandedErrors[tool.tool_name]
                                    ? 'Show less'
                                    : `+${tool.recent_errors.length - 3} more errors`}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom Legend with explanations */}
      {tools.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
          <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <span className="font-medium">Latency Percentiles:</span>
            </div>
            <Tooltip content="50% of calls are faster than this (typical experience)">
              <div className="flex items-center gap-1 cursor-help">
                <div className="w-3 h-3 bg-green-500 rounded-full" />
                <span>p50 (median)</span>
              </div>
            </Tooltip>
            <Tooltip content="95% of calls are faster than this (good benchmark for SLAs)">
              <div className="flex items-center gap-1 cursor-help">
                <div className="w-3 h-3 bg-yellow-400 rounded-full" />
                <span>p95</span>
              </div>
            </Tooltip>
            <Tooltip content="99% of calls are faster than this (worst case, excluding extreme outliers)">
              <div className="flex items-center gap-1 cursor-help">
                <div className="w-3 h-3 bg-red-300 rounded-full" />
                <span>p99</span>
              </div>
            </Tooltip>
            <div className="border-l border-gray-300 dark:border-gray-600 pl-4 ml-2">
              <Tooltip content="Success rate indicators">
                <div className="flex items-center gap-3 cursor-help">
                  <span className="flex items-center gap-1"><span className="text-green-600">Green</span> &ge;95%</span>
                  <span className="flex items-center gap-1"><span className="text-yellow-600">Yellow</span> 80-95%</span>
                  <span className="flex items-center gap-1"><span className="text-red-600">Red</span> &lt;80%</span>
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolsPage;
